// DTOs/schemas dos destinos de eventos de saída (webhook, RabbitMQ, NATS, Kafka, SQS) e do
// catálogo de eventos. Pertence ao pacote eventos-saida.
import { z } from "zod";

import { EVENTOS_SAIDA, zEventoSaida, type EventoSaidaNome, type TipoNumero } from "./enums";

/** Teto de destinos por conta (todos os tipos somados). */
export const MAX_DESTINOS_POR_CONTA = 5;

export const TIPOS_DESTINO_EVENTO = ["WEBHOOK", "RABBITMQ", "NATS", "KAFKA", "SQS"] as const;
export type TipoDestinoEventoNome = (typeof TIPOS_DESTINO_EVENTO)[number];

// ─── Catálogo ───────────────────────────────────────────────────────────────

export interface EventoCatalogo {
  nome: EventoSaidaNome;
  descricao: string;
  /** Tipos de número em que o evento ocorre (vazio = evento da conta, sem número). */
  tiposNumero: TipoNumero[];
  /** Versão do formato de `dados`. Muda só com quebra de compatibilidade. */
  versao: number;
}

const AMBOS: TipoNumero[] = ["OFICIAL", "NAO_OFICIAL"];

/** Metadados de cada evento de `EVENTOS_SAIDA`. O `Record` obriga a cobrir todos. */
const METADADOS_EVENTOS: Record<EventoSaidaNome, Omit<EventoCatalogo, "nome">> = {
  "mensagem.recebida": { descricao: "Mensagem recebida de um contato.", tiposNumero: AMBOS, versao: 1 },
  "mensagem.enviada": { descricao: "Mensagem enviada pelo número (API, painel ou celular).", tiposNumero: AMBOS, versao: 1 },
  "mensagem.status": { descricao: "Mudança de status de uma mensagem enviada (entregue, lida, falhou).", tiposNumero: AMBOS, versao: 1 },
  "numero.status": { descricao: "Número conectou, desconectou ou mudou de status.", tiposNumero: AMBOS, versao: 1 },
  "numero.qrcode": { descricao: "Novo QR Code para conectar um número não oficial.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "contato.optout": { descricao: "Contato pediu para não receber mais mensagens.", tiposNumero: AMBOS, versao: 1 },
  "campanha.status": { descricao: "Campanha mudou de status ou terminou.", tiposNumero: ["OFICIAL"], versao: 1 },
  "lead.criado": { descricao: "Lead captado por um número oficial.", tiposNumero: ["OFICIAL"], versao: 1 },
  "lead.repassado": { descricao: "Lead repassado para um número não oficial.", tiposNumero: AMBOS, versao: 1 },
  "lead.status": { descricao: "Lead mudou de status (respondeu, sem resposta, descartado).", tiposNumero: AMBOS, versao: 1 },
  "mensagem.reacao": { descricao: "Reação a uma mensagem (emoji vazio = reação removida).", tiposNumero: AMBOS, versao: 1 },
  "mensagem.editada": { descricao: "O autor editou uma mensagem de texto.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "mensagem.apagada": { descricao: "O autor apagou uma mensagem para todos.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "mensagem.voto": { descricao: "Um contato votou (ou trocou o voto) numa enquete enviada pelo número.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "contato.digitando": { descricao: "O contato está digitando, gravando áudio ou parou.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "contato.presenca": { descricao: "O contato ficou online ou offline (conforme a privacidade dele).", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "contato.atualizado": { descricao: "O contato mudou nome público, foto ou recado.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "chamada.recebida": { descricao: "O número recebeu uma chamada de voz ou vídeo.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "chamada.encerrada": { descricao: "Uma chamada terminou.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "grupo.atualizado": { descricao: "Nome, descrição ou configurações de um grupo mudaram.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "grupo.participantes": { descricao: "Participantes entraram, saíram, foram promovidos ou rebaixados num grupo.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "grupo.entrou": { descricao: "O número entrou num grupo (convite, link ou criação).", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "etiqueta.alterada": { descricao: "Uma etiqueta do WhatsApp Business foi criada, editada ou removida.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "etiqueta.aplicada": { descricao: "Uma etiqueta foi aplicada ou removida de uma conversa ou mensagem.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "canal.seguido": { descricao: "O número passou a seguir um canal (newsletter).", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "canal.deixado": { descricao: "O número deixou de seguir um canal (newsletter).", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "numero.banido": { descricao: "O WhatsApp aplicou banimento temporário ao número.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "historico.sincronizado": { descricao: "O celular concluiu uma etapa de sincronização de histórico.", tiposNumero: ["NAO_OFICIAL"], versao: 1 },
  "evolution.bruto": {
    descricao: "Evento original do Evolution Go, sem credenciais nem base64. O formato acompanha a versão do Evolution.",
    tiposNumero: ["NAO_OFICIAL"],
    versao: 1,
  },
};

/** Catálogo de eventos (`GET /v1/eventos/catalogo`), na ordem de `EVENTOS_SAIDA`. */
export const CATALOGO_EVENTOS: EventoCatalogo[] = EVENTOS_SAIDA.map((nome) => ({ nome, ...METADADOS_EVENTOS[nome] }));

export interface CatalogoEventosDto {
  eventos: EventoCatalogo[];
  tiposDestino: TipoDestinoEventoNome[];
  /** O WebSocket não é destino cadastrado: é uma conexão em `/v1/eventos/ws`. */
  websocket: { url: string };
}

export const STATUS_ENTREGA_EVENTO = ["PENDENTE", "ENTREGUE", "NAO_ROTEADA", "FALHOU"] as const;
export type StatusEntregaEventoNome = (typeof STATUS_ENTREGA_EVENTO)[number];

/** Evento técnico enviado pelo botão "Testar". Não é assinável (fora de `EVENTOS_SAIDA`). */
export const EVENTO_TESTE_PING = "teste.ping" as const;

export const MODOS_RABBIT = ["exchange", "filas"] as const;
export type ModoRabbit = (typeof MODOS_RABBIT)[number];
export const AUTENTICACOES_NATS = ["nenhuma", "token", "usuario", "creds"] as const;
export type AutenticacaoNats = (typeof AUTENTICACOES_NATS)[number];
export const MECANISMOS_SASL = ["nenhum", "plain", "scram-sha-256", "scram-sha-512"] as const;
export type MecanismoSasl = (typeof MECANISMOS_SASL)[number];

/**
 * Envelope ÚNICO dos eventos, igual em webhook e RabbitMQ.
 *  - `id`: publicId do evento, estável entre tentativas e entre destinos (deduplicação).
 *  - `sequencia`: crescente POR NÚMERO (para o cliente reordenar). Eventos sem número
 *    têm `sequencia` 0 — ordene-os por `ocorridoEm`.
 */
export interface EnvelopeEvento {
  id: string;
  evento: EventoSaidaNome | typeof EVENTO_TESTE_PING;
  ocorridoEm: string;
  contaId: string;
  numeroId: string | null;
  sequencia: number;
  dados: Record<string, unknown>;
}

// ─── Entrada ────────────────────────────────────────────────────────────────

const nome = z.string().trim().min(1, "Informe um nome.").max(80, "Máximo de 80 caracteres.");

const eventos = z
  .array(zEventoSaida, { invalid_type_error: "Informe a lista de eventos." })
  .min(1, "Assine pelo menos um evento.")
  .max(EVENTOS_SAIDA.length)
  .transform((lista) => [...new Set(lista)]);

/** publicIds dos números. Vazio = todos os números da conta. */
const numeroIds = z.array(z.string().trim().min(1).max(64)).max(100).transform((l) => [...new Set(l)]);

const exchange = z
  .string()
  .trim()
  .min(1, "Informe a exchange.")
  .max(255)
  .regex(/^[A-Za-z0-9_.:\-]+$/, "Use apenas letras, números e _ . : -");

/** Prefixo de routing key do RabbitMQ (pode ser vazio). */
export const REGEX_PREFIXO_RABBIT = /^([A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*)?$/;
/** Prefixo de assunto do NATS: palavras separadas por ponto, sem curingas, não vazio. */
export const REGEX_PREFIXO_NATS = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)*$/;

const prefixo = z
  .string()
  .trim()
  .max(64)
  .regex(REGEX_PREFIXO_RABBIT, "Use palavras de letras, números, _ ou - separadas por ponto.");

const porta = z.coerce.number().int().min(1).max(65535);
const host = z.string().trim().min(1, "Informe o host.").max(253);
const uriAmqp = z
  .string()
  .trim()
  .max(2048)
  .regex(/^amqps?:\/\//i, "A URI deve começar com amqps:// (ou amqp://).");

export const zCriarDestinoWebhook = z.object({
  tipo: z.literal("WEBHOOK"),
  nome,
  ativo: z.boolean().default(true),
  eventos,
  numeroIds: numeroIds.default([]),
  /** HTTPS obrigatório; host não pode resolver para IP interno. */
  url: z.string().trim().min(1, "Informe a URL.").max(2048),
});

export const zCriarDestinoRabbit = z
  .object({
    tipo: z.literal("RABBITMQ"),
    nome,
    ativo: z.boolean().default(true),
    eventos,
    numeroIds: numeroIds.default([]),
    /** Alternativa aos campos de conexão: `amqps://usuario:senha@host:5671/vhost`. */
    uri: uriAmqp.optional(),
    host: host.optional(),
    port: porta.optional(),
    vhost: z.string().trim().max(255).optional(),
    usuario: z.string().min(1).max(255).optional(),
    senha: z.string().min(1).max(1024).optional(),
    /** TLS (`amqps`). Padrão true. Com false exige `permitirSemTls`. */
    tls: z.boolean().optional(),
    exchange,
    prefixo: prefixo.default("zaphub"),
    /** Confirmação explícita do OWNER para conectar SEM TLS (`amqp`). */
    permitirSemTls: z.boolean().default(false),
    /** `exchange`: só publica. `filas`: o ZapHub declara uma fila durável por evento (`<prefixo>.<evento>`). */
    modo: z.enum(MODOS_RABBIT).default("exchange"),
  })
  .superRefine((d, ctx) => {
    const porCampos = Boolean(d.host && d.usuario && d.senha);
    if (!d.uri && !porCampos) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["uri"],
        message: "Informe a URI do broker ou host, usuário e senha.",
      });
    }
    if (d.uri && (d.host || d.usuario || d.senha)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["uri"],
        message: "Use a URI OU os campos host/usuário/senha, não os dois.",
      });
    }
    const semTls = d.uri ? /^amqp:\/\//i.test(d.uri) : d.tls === false;
    if (semTls && !d.permitirSemTls) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["permitirSemTls"],
        message: "Conexão sem TLS exige confirmação explícita (permitirSemTls).",
      });
    }
  });

/** `host:porta` (aceita `nats://`, `tls://`; IPv6 entre colchetes). */
const enderecoServidor = z
  .string()
  .trim()
  .min(3)
  .max(300)
  .regex(/^((nats|tls):\/\/)?(\[[0-9a-fA-F:]+\]|[A-Za-z0-9.-]+):\d{1,5}$/, "Use host:porta (ex.: nats.exemplo.com:4222).");

const enderecoBroker = z
  .string()
  .trim()
  .min(3)
  .max(300)
  .regex(/^(\[[0-9a-fA-F:]+\]|[A-Za-z0-9.-]+):\d{1,5}$/, "Use host:porta (ex.: kafka.exemplo.com:9093).");

const topicoKafka = z
  .string()
  .trim()
  .min(1)
  .max(200)
  .regex(/^[A-Za-z0-9._-]+$/, "Use letras, números, ponto, _ ou -.");

/** Prefixo de assunto NATS: palavras separadas por ponto, sem curingas. */
const prefixoNats = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(REGEX_PREFIXO_NATS, "Use palavras de letras, números, _ ou - separadas por ponto.");

/** URL de fila SQS: `https://sqs.<regiao>.amazonaws.com/<conta>/<fila>`; a região sai dela. */
export const REGEX_FILA_SQS = /^https:\/\/sqs\.([a-z]{2}(?:-gov)?-[a-z]+-\d)\.amazonaws\.com\/(\d{12})\/([A-Za-z0-9_-]{1,80}(?:\.fifo)?)$/;
const filaSqs = z.string().trim().max(300).regex(REGEX_FILA_SQS, "URL da fila inválida (https://sqs.<região>.amazonaws.com/<conta>/<fila>).");
const accessKeyId = z.string().trim().regex(/^[A-Z0-9]{16,128}$/, "Access key id inválido.");

const camposComuns = {
  nome,
  ativo: z.boolean().default(true),
  eventos,
  numeroIds: numeroIds.default([]),
};

export const zCriarDestinoNats = z
  .object({
    tipo: z.literal("NATS"),
    ...camposComuns,
    servidores: z.array(enderecoServidor).min(1, "Informe pelo menos um servidor.").max(5),
    /** TLS. Padrão true. Com false exige `permitirSemTls`. */
    tls: z.boolean().default(true),
    permitirSemTls: z.boolean().default(false),
    autenticacao: z.enum(AUTENTICACOES_NATS).default("nenhuma"),
    usuario: z.string().min(1).max(255).optional(),
    /** Token, senha ou conteúdo do arquivo `.creds`, conforme `autenticacao`. */
    senha: z.string().min(1).max(8192).optional(),
    prefixo: prefixoNats.default("zaphub"),
    /** Publica pelo JetStream (aguarda ack, deduplica por `Nats-Msg-Id`). */
    jetstream: z.boolean().default(false),
  })
  .superRefine((d, ctx) => {
    exigirTls(d.tls, d.permitirSemTls, ctx);
    if (d.autenticacao === "usuario" && !(d.usuario && d.senha)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["senha"], message: "Informe usuário e senha." });
    }
    if ((d.autenticacao === "token" || d.autenticacao === "creds") && !d.senha) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["senha"],
        message: d.autenticacao === "token" ? "Informe o token." : "Cole o conteúdo do arquivo .creds.",
      });
    }
  });

export const zCriarDestinoKafka = z
  .object({
    tipo: z.literal("KAFKA"),
    ...camposComuns,
    brokers: z.array(enderecoBroker).min(1, "Informe pelo menos um broker.").max(10),
    tls: z.boolean().default(true),
    permitirSemTls: z.boolean().default(false),
    sasl: z.enum(MECANISMOS_SASL).default("nenhum"),
    usuario: z.string().min(1).max(255).optional(),
    senha: z.string().min(1).max(1024).optional(),
    /** Tópico único para todos os eventos. */
    topico: topicoKafka.optional(),
    /** Um tópico por evento: `<prefixoTopico>-<evento com . trocado por ->` (ex.: `zaphub-mensagem-recebida`). */
    prefixoTopico: topicoKafka.optional(),
  })
  .superRefine((d, ctx) => {
    exigirTls(d.tls, d.permitirSemTls, ctx);
    if (Boolean(d.topico) === Boolean(d.prefixoTopico)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["topico"], message: "Informe o tópico OU o prefixo de tópico." });
    }
    if (d.sasl !== "nenhum" && !(d.usuario && d.senha)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["senha"], message: "Informe usuário e senha do SASL." });
    }
  });

export const zCriarDestinoSqs = z.object({
  tipo: z.literal("SQS"),
  ...camposComuns,
  filaUrl: filaSqs,
  accessKeyId,
  secretAccessKey: z.string().min(16).max(256),
});

function exigirTls(tls: boolean, permitirSemTls: boolean, ctx: z.RefinementCtx): void {
  if (!tls && !permitirSemTls) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["permitirSemTls"],
      message: "Conexão sem TLS exige confirmação explícita (permitirSemTls).",
    });
  }
}

export type CriarDestinoWebhook = z.output<typeof zCriarDestinoWebhook>;
export type CriarDestinoRabbit = z.output<typeof zCriarDestinoRabbit>;
export type CriarDestinoNats = z.output<typeof zCriarDestinoNats>;
export type CriarDestinoKafka = z.output<typeof zCriarDestinoKafka>;
export type CriarDestinoSqs = z.output<typeof zCriarDestinoSqs>;
export type CriarDestinoEvento =
  | CriarDestinoWebhook
  | CriarDestinoRabbit
  | CriarDestinoNats
  | CriarDestinoKafka
  | CriarDestinoSqs;

const SCHEMAS_CRIACAO = {
  WEBHOOK: zCriarDestinoWebhook,
  RABBITMQ: zCriarDestinoRabbit,
  NATS: zCriarDestinoNats,
  KAFKA: zCriarDestinoKafka,
  SQS: zCriarDestinoSqs,
} as const;

/**
 * Corpo do `POST /destinos-eventos`. Escolhe o schema pelo `tipo` (o refinamento do
 * RabbitMQ não cabe num `discriminatedUnion`) e repassa os erros de campo do escolhido.
 */
export const zCriarDestino = z.unknown().transform((entrada, ctx): CriarDestinoEvento => {
  const tipo = (entrada as { tipo?: unknown } | null)?.tipo;
  const schema = typeof tipo === "string" ? SCHEMAS_CRIACAO[tipo as TipoDestinoEventoNome] : undefined;
  if (!schema) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["tipo"],
      message: `O tipo deve ser ${TIPOS_DESTINO_EVENTO.join(", ")}.`,
    });
    return z.NEVER;
  }
  const r = (schema as z.ZodType<CriarDestinoEvento>).safeParse(entrada);
  if (!r.success) {
    for (const issue of r.error.issues) ctx.addIssue(issue);
    return z.NEVER;
  }
  return r.data;
});

/** PATCH: todos os campos opcionais; o `tipo` do destino não muda. */
export const zAtualizarDestino = z.object({
  nome: nome.optional(),
  ativo: z.boolean().optional(),
  eventos: eventos.optional(),
  numeroIds: numeroIds.optional(),
  url: z.string().trim().min(1).max(2048).optional(),
  uri: uriAmqp.optional(),
  host: host.optional(),
  port: porta.optional(),
  vhost: z.string().trim().max(255).optional(),
  usuario: z.string().min(1).max(255).optional(),
  senha: z.string().min(1).max(8192).optional(),
  tls: z.boolean().optional(),
  exchange: exchange.optional(),
  /** RabbitMQ usa o regex de routing key; NATS o de assunto (validado no serviço). */
  prefixo: z.string().trim().max(64).optional(),
  permitirSemTls: z.boolean().optional(),
  modo: z.enum(MODOS_RABBIT).optional(),
  // NATS
  servidores: z.array(enderecoServidor).min(1).max(5).optional(),
  autenticacao: z.enum(AUTENTICACOES_NATS).optional(),
  jetstream: z.boolean().optional(),
  // Kafka
  brokers: z.array(enderecoBroker).min(1).max(10).optional(),
  sasl: z.enum(MECANISMOS_SASL).optional(),
  /** `null` remove (para trocar tópico único por prefixo, ou o contrário). */
  topico: topicoKafka.nullable().optional(),
  prefixoTopico: topicoKafka.nullable().optional(),
  // SQS
  filaUrl: filaSqs.optional(),
  accessKeyId: accessKeyId.optional(),
  secretAccessKey: z.string().min(16).max(256).optional(),
  /** Gera um novo segredo HMAC (devolvido UMA vez na resposta). */
  rotacionarSegredo: z.boolean().optional(),
});
export type AtualizarDestino = z.output<typeof zAtualizarDestino>;

export const zListarEntregas = z.object({
  pagina: z.coerce.number().int().min(1).default(1),
  limite: z.coerce.number().int().min(1).max(100).default(20),
  status: z.enum(STATUS_ENTREGA_EVENTO).optional(),
});
export type ListarEntregas = z.output<typeof zListarEntregas>;

// ─── Saída ──────────────────────────────────────────────────────────────────

export interface ConfigWebhookPublica {
  url: string;
}

export interface ConfigRabbitPublica {
  host: string;
  port: number;
  vhost: string;
  /** Mascarado (`••••1234`). */
  usuario: string;
  /** Sempre `••••••••`. */
  senha: string;
  exchange: string;
  prefixo: string;
  tls: boolean;
  permitirSemTls: boolean;
  modo: ModoRabbit;
}

export interface ConfigNatsPublica {
  servidores: string[];
  tls: boolean;
  permitirSemTls: boolean;
  autenticacao: AutenticacaoNats;
  /** Mascarado; `null` sem autenticação por usuário. */
  usuario: string | null;
  /** `••••••••` quando há token/senha/creds; `null` sem autenticação. */
  senha: string | null;
  prefixo: string;
  jetstream: boolean;
}

export interface ConfigKafkaPublica {
  brokers: string[];
  tls: boolean;
  permitirSemTls: boolean;
  sasl: MecanismoSasl;
  usuario: string | null;
  senha: string | null;
  topico: string | null;
  prefixoTopico: string | null;
}

export interface ConfigSqsPublica {
  filaUrl: string;
  regiao: string;
  fifo: boolean;
  /** Mascarado. */
  accessKeyId: string;
  /** Sempre `••••••••`. */
  secretAccessKey: string;
}

export type ConfigDestinoPublica =
  | ConfigWebhookPublica
  | ConfigRabbitPublica
  | ConfigNatsPublica
  | ConfigKafkaPublica
  | ConfigSqsPublica;

export interface DestinoEventoDto {
  /** publicId. */
  id: string;
  tipo: TipoDestinoEventoNome;
  nome: string;
  ativo: boolean;
  eventos: EventoSaidaNome[];
  /** publicIds dos números; vazio = todos. */
  numeroIds: string[];
  config: ConfigDestinoPublica;
  /** Segredo HMAC mascarado (`••••1234`). O completo só aparece na criação/rotação. */
  segredoHmac: string;
  criadoEm: string;
  atualizadoEm: string;
}

/** Resposta de criar/rotacionar: o segredo HMAC completo é exibido UMA vez. */
export interface DestinoEventoCriadoDto extends DestinoEventoDto {
  segredoHmacRevelado?: string;
}

export interface ResultadoTesteDestino {
  sucesso: boolean;
  /** Status HTTP da resposta (só webhook). */
  statusHttp: number | null;
  latenciaMs: number;
  /** Mensagem de erro exata (sem segredos). */
  erro: string | null;
  /** Ex.: exchange sem fila ligada à routing key do ping. */
  aviso: string | null;
}

export interface EntregaEventoDto {
  /** Identificador opaco da entrega (`<eventoPublicId>.<destinoPublicId>`). */
  id: string;
  eventoId: string;
  evento: string;
  sequencia: number;
  status: StatusEntregaEventoNome;
  tentativas: number;
  proximaTentativaEm: string | null;
  ultimoErro: string | null;
  entregueEm: string | null;
  criadoEm: string;
}

export interface ListaEntregasDto {
  itens: EntregaEventoDto[];
  total: number;
  pagina: number;
  limite: number;
}

// ─── WebSocket de eventos (`/v1/eventos/ws`) ─────────────────────────────────

/** Validade do ticket de conexão (uso único). */
export const TTL_TICKET_WS_S = 60;
/** Conexões simultâneas por chave de API. */
export const MAX_CONEXOES_WS_POR_CHAVE = 5;
/** Código de fechamento quando a chave já tem o máximo de conexões. */
export const CODIGO_WS_CONEXOES_DEMAIS = 4429;
/** Janela de retomada por `desde`. */
export const JANELA_RETOMADA_WS_MS = 24 * 3600_000;

export interface TicketWsDto {
  ticket: string;
  expiraEmSegundos: number;
}

/** Primeira mensagem do cliente depois de conectar. */
export const zAssinarWs = z.object({
  tipo: z.literal("assinar"),
  /** Omitido = `EVENTOS_PADRAO` (todos menos o bruto e a presença, que só saem se pedidos). */
  eventos: z.array(zEventoSaida).min(1).max(EVENTOS_SAIDA.length).optional(),
  /** publicIds dos números; omitido/vazio = todos os permitidos à chave. */
  numeroIds: z.array(z.string().trim().min(1).max(64)).max(100).optional(),
  /** `id` do último evento recebido: reenvia os posteriores (até 24h). */
  desde: z.string().trim().min(1).max(64).optional(),
});
export type AssinarWs = z.output<typeof zAssinarWs>;

/** Mensagens de controle do servidor (os eventos chegam como o envelope, sem `tipo`). */
export type MensagemControleWs =
  | { tipo: "assinado"; eventos: EventoSaidaNome[]; numeroIds: string[]; retomados: number }
  | { tipo: "erro"; codigo: string; mensagem: string };
